import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { HttpPagination } from '@library/common';
import { IGenericType } from '@library/common/generictype';
import { BehaviorSubject, map, Observable, of, OperatorFunction, pipe, startWith, Subject, switchMap, take, tap } from 'rxjs';
import * as crypto from 'crypto-js';

export const DefaultExpiryLength = 5; // Minutes

interface IRequestOptions<T, R> {
  url: string;
  params?: IGenericType;
  headers?: IGenericType;
  useLambda?: boolean;
  useLocalLambda?: boolean;
  length?: number; // hours for this call to be expired
  pipe?: OperatorFunction<T, R>;
}

interface IRqstOpts<T, R> extends IRequestOptions<T, R> {
  pagination?: IGenericType;
  expiry?: number;
  data?: IGenericType | IGenericType[];
}


@Injectable({
  providedIn: 'root'
})
export class CacheRequestService {

  optsCache = new Map<string, IRqstOpts<any, any>>();
  subsCache = new Map<string, Observable<any>>();

  constructor(
    public pagination: HttpPagination,
    private http: HttpClient,
  ) { }

  request<T, R>( opts: IRequestOptions<T, R> ) {
    const ret$ = new BehaviorSubject<R>( [] as unknown as R );
    const refresh$ = new Subject<void>();

    const piper: OperatorFunction<T, R> = opts.pipe || (pipe() as OperatorFunction<any, any>);

    let key = crypto.SHA256(`${ opts.url }-${ opts.params ? JSON.stringify( opts.params ) : '' }`)
      .toString( crypto.enc.Hex );

    let paused = false;
    let stopped = false;
    let paginated = false;

    refresh$
      .pipe(
        startWith( true ),
        // call the endpoint
        switchMap( _ => {
          let options: IRqstOpts<T, R> = this.optsCache.get( key );
          let req$: Observable<any> = this.subsCache.get( key );

          // Check if the options exists on the cache, and not expired
          if ( options == null || options.expiry <= Date.now() ) {
            // Set other fields
            options = this.initOpts( opts ); // either if the cache expires or not existing

            // Delete the cache if they exists
            this.optsCache.delete( key );
            this.subsCache.delete( key );
          } else if ( options != null && options.expiry > Date.now() && options.pagination.page > options.pagination.total ) {
            options.pagination.page--; // decrement page for the increment later on
            
            req$ = of({
              type: 'quote',
              messages: [],
              response: {
                pagination: options.pagination,
                collection: options.data,
              },
            });
          }

          // Check if the request does not exists
          if ( req$ == null ) {
            // create the request
            req$ = (options.params ?
              this.http.post( options.url, options.params, { headers: options.headers, params: { useLambda: options.useLambda, useLocalLambda: options.useLocalLambda } } ) :
              this.http.get( options.url, { headers: options.headers, params: { useLambda: options.useLambda, useLocalLambda: options.useLocalLambda } } ))
              .pipe(
                tap( _ => this.subsCache.delete( key ) ),
                take( 1 ),
              );

            // and store it to the cache
            this.subsCache.set( key, req$ );
          }

          return req$;
        } ),
        map( ( response: IGenericType ) => {
          // check if the response got pagination
          if ( response?.response?.pagination ) {
            paginated = true;

            if ( response.response.pagination.page === 1 ) {
              // new pagination request, clear what we have cached (if anything)

              this.optsCache.delete( key );
            }

            const cached = this.optsCache.get( key );

            // increment for next page
            response.response.pagination.page++;

            // reinstate the options cache
            this.optsCache.set( key, ({
              ...this.initOpts( opts ),
              data: cached?.data || [],
              params: { pagination: response.response.pagination },
              pagination: response.response.pagination,
            }));
          }

          const cached = this.optsCache.get( key );

          if ( cached ) {
            response.response.collection = [
              ...response.response.collection,
              ...( cached.data || [] )
            ];
          }

          return response;
        }),
        piper,
        tap( data => {
          // Save to cache
          const options = this.initOpts( this.optsCache.get( key ) || opts );

          this.optsCache.set( key, ({
            ...options,
            data,
          }) );
        }),
        tap( _ => this.subsCache.delete( key ) ), // remove the subs cache after
        // Refresh if this is not stopped, paused and is paginated
        tap( _ => { 
          const cache = this.optsCache.get( key );
          
          // Check if this is not stopped, paused and is paginated
          if ( !stopped && !paused && paginated &&
            // Check if this is already at the end of total pages
            cache.pagination?.page <= cache.pagination?.total ) {

            this.pagination.hash = cache.pagination?.hash;
            this.pagination.loading = true;
            this.pagination.loadingPercent = +( ( cache.pagination?.page - 1 ) / cache.pagination?.total * 100 ).toFixed();
            
            refresh$.next();
          } else {
            // paused, stopped, or at the end of pages

            this.pagination.reset();
          }
        } ),
        // shareReplay(),
      ).subscribe(( response ) => {
        ret$.next( response );
      });

    return {
      response$: ret$,
      pause: () => { paused = true; },
      stop: () => {
        stopped = true;
      },
      continue: () => {
        if ( !stopped ) {
          paused = false;
          refresh$.next();
        }
      },
      reset: () => {
        paused = false;
        stopped = false;

        // Free up keys
        this.optsCache.delete( key );
        this.subsCache.delete( key );
      },
    };
  }

  private initOpts<T, R>( opts: IRequestOptions<T, R> ) {
    let options: IRqstOpts<T, R> = JSON.parse( JSON.stringify( opts ) );

    options.length = options.length || DefaultExpiryLength;
    options.expiry = Date.now() + ( options.length * 60 * 1000 );

    return options;
  }

}
